import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import Styles from './ManageIntegrationAccounts.module.scss'
import { useConnectedAccountCardProps } from './hooks'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import CircleCheck from 'almanac-ui/icons/16/CircleCheck'
import { getIntegrationDetails } from 'components/Previews/getIntegrationDetails'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Spinner } from 'common/Spinner/Spinner'
import { ArrowRefresh2, Close, AlertTriangle } from 'blaze-ui/icons'
import { Settings } from 'blaze-ui/icons/20'
import cx from 'classnames'
import { ErrorBoundary } from '@sentry/react'

export interface ConnectedAccountCardProps {
  integration: ExternalIntegration
  className?: string
  isDisconnected?: boolean
}

const ConnectedAccountCard = ({ integration, className, isDisconnected = false }: ConnectedAccountCardProps) => {
  const {
    name,
    icon,
    avatarUrl,
    details,
    onRefreshConnection,
    onDisconnectAccount,
    isDeleting,
    showSettings,
    onConfigureClick,
  } = useConnectedAccountCardProps(integration)

  const integrationName = getIntegrationDetails(integration).username

  return (
    <div className={cx(Styles.connectedAccountCard, className, { [Styles.disconnected]: isDisconnected })}>
      <div className={Styles.cardTop}>
        <div className={Styles.iconsContainer}>
          <div className={Styles.icons}>
            <img src={avatarUrl || Silhouette} alt="profile" className={Styles.avatar} />
            {icon}
          </div>
          <div className={Styles.topActions}>
            {isDisconnected && (
              <div className={Styles.warningIcon} data-testid="integration-disconnected-warning">
                <AlertTriangle size={20} color="var(--negative-50)" />
              </div>
            )}
            {showSettings && (
              <Button
                variant="tertiary"
                size="xs"
                onPress={onConfigureClick}
                aria-label={`Configure ${name} integration`}
                frontIcon={Settings}
                className={Styles.gearButton}
              />
            )}
          </div>
        </div>
        <div className={Styles.info}>
          <Heading level={4} lineClamp={1}>
            {name}
          </Heading>
          {details && <Text variant="label">{details}</Text>}
          <Tooltip label={integration.provider_id}>
            <div className={Styles.providerId}>
              <Text variant="placeholder" lineClamp={1}>
                ID: {integration.provider_id}
              </Text>
            </div>
          </Tooltip>
          {integrationName !== name && (
            <div className={Styles.integration}>
              <CircleCheck size={16} color="var(--green-50)" />
              <Text variant="metadata" color="var(--green-50)" lineClamp={1}>
                {getIntegrationDetails(integration).username}
              </Text>
            </div>
          )}
        </div>
      </div>
      <div className={Styles.actions}>
        {integration.provider !== ExternalIntegrationEnum.WordPress && (
          <Button
            variant="tertiary"
            size="sm"
            onPress={() => onRefreshConnection()}
            isDisabled={isDeleting}
            frontIcon={ArrowRefresh2}
            className={cx({ [Styles.disconnectedRefreshButton]: isDisconnected })}
          >
            Refresh
          </Button>
        )}
        <Button
          variant="tertiary"
          size="sm"
          onPress={onDisconnectAccount}
          isDisabled={isDeleting}
          frontIcon={isDeleting ? undefined : Close}
        >
          {isDeleting ? (
            <div className={Styles.loadingState}>
              <Spinner />
              <span>Disconnecting...</span>
            </div>
          ) : (
            'Disconnect'
          )}
        </Button>
      </div>
    </div>
  )
}

const WithError = (props: ConnectedAccountCardProps) => (
  <ErrorBoundary>
    <ConnectedAccountCard {...props} />
  </ErrorBoundary>
)

export { WithError as ConnectedAccountCard }
